import * as types from '../mutation-types';
import Report from '../../api/reports';
import { throwErrorMessage } from '../utils/api';
import { downloadCsvFile } from '../../helper/downloadHelper';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { REPORTS_EVENTS } from '../../helper/AnalyticsHelper/events';

export const state = {
  records: {
    data: null,
    lastUpdate: null,
    timeline: {
      items: [],
      page: 1,
      perPage: 50,
      totalCount: 0,
      hasMore: false,
    },
    reassignments: {
      items: [],
      page: 1,
      perPage: 50,
      totalCount: 0,
      hasMore: false,
    },
  },
  filters: {
    from: 0,
    to: 0,
    teamId: null,
    inboxId: null,
    agentIds: null,
    selectedAgentId: null,
  },
  uiFlags: {
    isFetching: false,
    isExporting: false,
    isFetchingTimeline: false,
    isFetchingReassignments: false,
  },
};

export const getters = {
  getReportData($state) {
    return $state.records.data;
  },

  getLastUpdate($state) {
    return $state.records.lastUpdate;
  },

  getUIFlags($state) {
    return $state.uiFlags;
  },

  getFilters($state) {
    return $state.filters;
  },

  // Timeline getters
  getTimeline($state) {
    return $state.records.timeline.items;
  },

  getTimelinePagination($state) {
    return {
      page: $state.records.timeline.page,
      perPage: $state.records.timeline.perPage,
      totalCount: $state.records.timeline.totalCount,
      hasMore: $state.records.timeline.hasMore,
    };
  },

  // Filtered timeline by selected agent
  getFilteredTimeline: ($state) => {
    const { selectedAgentId } = $state.filters;
    if (!selectedAgentId) return $state.records.timeline.items;

    return $state.records.timeline.items.filter(
      item => item.assignee?.id === selectedAgentId
    );
  },

  // Reassignments getters
  getReassignments($state) {
    return $state.records.reassignments.items;
  },

  getReassignmentsPagination($state) {
    return {
      page: $state.records.reassignments.page,
      perPage: $state.records.reassignments.perPage,
      totalCount: $state.records.reassignments.totalCount,
      hasMore: $state.records.reassignments.hasMore,
    };
  },

  // Filtered reassignments by selected agent
  getFilteredReassignments: ($state) => {
    const { selectedAgentId } = $state.filters;
    const { data } = $state.records;

    if (!selectedAgentId || !data) return $state.records.reassignments.items;

    const allAgents = [
      ...(data.online_agents || []),
      ...(data.offline_agents || [])
    ];
    const selectedAgent = allAgents.find(a => a.id === selectedAgentId);
    const agentName = selectedAgent?.name;

    if (!agentName) return $state.records.reassignments.items;

    return $state.records.reassignments.items.filter(
      r => r.assigned_to === agentName || r.assigned_by === agentName
    );
  },

  // Selected agent getter
  getSelectedAgent: ($state) => {
    const { selectedAgentId } = $state.filters;
    const { data } = $state.records;

    if (!selectedAgentId || !data) return null;

    const allAgents = [
      ...(data.online_agents || []),
      ...(data.offline_agents || [])
    ];

    return allAgents.find(a => a.id === selectedAgentId);
  },

  // Metrics with agent filter
  getDisplayMetrics: ($state, getters) => {
    const { data } = $state.records;
    if (!data?.metrics) return {};

    const selectedAgent = getters.getSelectedAgent;
    if (!selectedAgent) return data.metrics;

    return {
      ...data.metrics,
      online_agents: selectedAgent.is_online ? 1 : 0,
      offline_agents: selectedAgent.is_online ? 0 : 1,
      total_agents: 1,
      open_conversations: selectedAgent.open_count,
      pending_conversations: selectedAgent.pending_count,
      resolved_conversations: selectedAgent.resolved_count,
      total_assignments: selectedAgent.total_assigned,
      team_open_conversations: data.metrics.open_conversations,
      team_total_agents: data.metrics.total_agents,
      avg_first_response_time: data.metrics.avg_first_response_time || 0,
      avg_resolution_time: data.metrics.avg_resolution_time || 0,
    };
  },

  // Filtered agents
  getFilteredOnlineAgents: ($state) => {
    const { data } = $state.records;
    const { agentIds } = $state.filters;

    if (!data?.online_agents) return [];

    let agents = data.online_agents;

    if (agentIds) {
      const selectedIds = agentIds.split(',').map(id => parseInt(id, 10));
      agents = agents.filter(agent => selectedIds.includes(agent.id));
    }

    return agents;
  },

  getFilteredOfflineAgents: ($state) => {
    const { data } = $state.records;
    const { agentIds } = $state.filters;

    if (!data?.offline_agents) return [];

    let agents = data.offline_agents;

    if (agentIds) {
      const selectedIds = agentIds.split(',').map(id => parseInt(id, 10));
      agents = agents.filter(agent => selectedIds.includes(agent.id));
    }

    return agents;
  },

  // Performance data
  getPerformanceData: ($state, getters) => {
    const { data } = $state.records;
    if (!data?.online_agents) return [];

    const allAgents = [
      ...(data.online_agents || []),
      ...(data.offline_agents || [])
    ];

    let agents = allAgents;
    const { agentIds } = $state.filters;

    if (agentIds) {
      const selectedIds = agentIds.split(',').map(id => parseInt(id, 10));
      agents = agents.filter(agent => selectedIds.includes(agent.id));
    }

    return agents
      .map(agent => ({
        name: agent.name,
        resolution_rate: agent.performance?.resolution_rate || 0,
        total_handled: agent.performance?.total_handled || 0,
      }))
      .filter(agent => agent.total_handled > 0)
      .slice(0, 10);
  },
};

export const actions = {
  // Fetch main report data (sem polling, direto)
  fetchReport: async ({ commit, state: $state }) => {
    commit(types.default.SET_ROUND_ROBIN_REPORTS_UI_FLAG, { isFetching: true });

    try {
      const params = {
        from: $state.filters.from,
        to: $state.filters.to,
        teamId: $state.filters.teamId,
        inboxId: $state.filters.inboxId,
        agentIds: $state.filters.agentIds,
      };

      const response = await Report.getRoundRobinReport(params);


      // Data is ready
      commit(types.default.SET_ROUND_ROBIN_REPORT_DATA, response.data);
      commit(types.default.SET_ROUND_ROBIN_LAST_UPDATE, new Date());

      // Initialize timeline and reassignments from main data
      if (response.data.timeline) {
        commit(types.default.SET_TIMELINE_ITEMS, {
          items: response.data.timeline,
          page: 1,
          totalCount: response.data.timeline.length,
          hasMore: false,
        });
      }

      if (response.data.reassignments) {
        commit(types.default.SET_REASSIGNMENTS_ITEMS, {
          items: response.data.reassignments,
          page: 1,
          totalCount: response.data.reassignments.length,
          hasMore: false,
        });
      }

      commit(types.default.SET_ROUND_ROBIN_REPORTS_UI_FLAG, { isFetching: false });
      
      return response.data;
    } catch (error) {
      console.error('❌ [RoundRobin] Error:', error);
      commit(types.default.SET_ROUND_ROBIN_REPORTS_UI_FLAG, { isFetching: false });
      throwErrorMessage(error);
    }
  },

  // Export report as CSV
  exportReport: async ({ commit, state: $state }, fileName) => {
    commit(types.default.SET_ROUND_ROBIN_REPORTS_UI_FLAG, { isExporting: true });

    try {
      const params = {
        from: $state.filters.from,
        to: $state.filters.to,
        teamId: $state.filters.teamId,
        inboxId: $state.filters.inboxId,
        agentIds: $state.filters.agentIds,
      };

      const response = await Report.exportRoundRobinReport(params);
      downloadCsvFile(fileName, response.data);

      AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
        reportType: 'round_robin',
        businessHours: false,
      });

      commit(types.default.SET_ROUND_ROBIN_REPORTS_UI_FLAG, { isExporting: false });
      return response;
    } catch (error) {
      commit(types.default.SET_ROUND_ROBIN_REPORTS_UI_FLAG, { isExporting: false });
      throwErrorMessage(error);
    }
  },

  // Update filters
  updateFilters: ({ commit }, filters) => {
    commit(types.default.UPDATE_ROUND_ROBIN_FILTERS, filters);
  },

  // Set selected agent for filtering
  setSelectedAgent: ({ commit }, agentId) => {
    commit(types.default.SET_SELECTED_AGENT, agentId);
  },

  // Clear selected agent filter
  clearSelectedAgent: ({ commit }) => {
    commit(types.default.SET_SELECTED_AGENT, null);
  },

  // Load more timeline items (for pagination)
  loadMoreTimeline: async ({ commit, state: $state }) => {
    const { page, hasMore } = $state.records.timeline;
    if (!hasMore) return;

    commit(types.default.SET_ROUND_ROBIN_REPORTS_UI_FLAG, { isFetchingTimeline: true });

    try {
      const nextPage = page + 1;
      commit(types.default.SET_TIMELINE_PAGE, nextPage);
      commit(types.default.SET_ROUND_ROBIN_REPORTS_UI_FLAG, { isFetchingTimeline: false });
    } catch (error) {
      commit(types.default.SET_ROUND_ROBIN_REPORTS_UI_FLAG, { isFetchingTimeline: false });
      throwErrorMessage(error);
    }
  },

  // Load more reassignments (for pagination)
  loadMoreReassignments: async ({ commit, state: $state }) => {
    const { page, hasMore } = $state.records.reassignments;
    if (!hasMore) return;

    commit(types.default.SET_ROUND_ROBIN_REPORTS_UI_FLAG, { isFetchingReassignments: true });

    try {
      const nextPage = page + 1;
      commit(types.default.SET_REASSIGNMENTS_PAGE, nextPage);
      commit(types.default.SET_ROUND_ROBIN_REPORTS_UI_FLAG, { isFetchingReassignments: false });
    } catch (error) {
      commit(types.default.SET_ROUND_ROBIN_REPORTS_UI_FLAG, { isFetchingReassignments: false });
      throwErrorMessage(error);
    }
  },

  // Clear all data
  clearReport: ({ commit }) => {
    commit(types.default.CLEAR_ROUND_ROBIN_REPORT_DATA);
  },
};

export const mutations = {
  [types.default.SET_ROUND_ROBIN_REPORTS_UI_FLAG]($state, uiFlag) {
    $state.uiFlags = { ...$state.uiFlags, ...uiFlag };
  },

  [types.default.SET_ROUND_ROBIN_REPORT_DATA]($state, data) {
    $state.records.data = data;
  },

  [types.default.SET_ROUND_ROBIN_LAST_UPDATE]($state, timestamp) {
    $state.records.lastUpdate = timestamp;
  },

  [types.default.UPDATE_ROUND_ROBIN_FILTERS]($state, filters) {
    $state.filters = { ...$state.filters, ...filters };
  },

  [types.default.SET_SELECTED_AGENT]($state, agentId) {
    $state.filters.selectedAgentId = agentId;
  },

  [types.default.SET_TIMELINE_ITEMS]($state, { items, page, totalCount, hasMore }) {
    $state.records.timeline = {
      ...$state.records.timeline,
      items,
      page,
      totalCount,
      hasMore,
    };
  },

  [types.default.SET_TIMELINE_PAGE]($state, page) {
    $state.records.timeline.page = page;
  },

  [types.default.SET_REASSIGNMENTS_ITEMS]($state, { items, page, totalCount, hasMore }) {
    $state.records.reassignments = {
      ...$state.records.reassignments,
      items,
      page,
      totalCount,
      hasMore,
    };
  },

  [types.default.SET_REASSIGNMENTS_PAGE]($state, page) {
    $state.records.reassignments.page = page;
  },

  [types.default.CLEAR_ROUND_ROBIN_REPORT_DATA]($state) {
    $state.records = {
      data: null,
      lastUpdate: null,
      timeline: {
        items: [],
        page: 1,
        perPage: 50,
        totalCount: 0,
        hasMore: false,
      },
      reassignments: {
        items: [],
        page: 1,
        perPage: 50,
        totalCount: 0,
        hasMore: false,
      },
    };
    $state.filters.selectedAgentId = null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};